import {
  InspectionValues,
  OrderStatus,
  OrderTypes,
  StepTypes,
} from '@parallel-fluidics/shared/constants';
import { travelerFromFirestore } from '@parallel-fluidics/shared/models/travelers';
import {
  collection,
  getFirestore,
  query,
  where,
} from 'firebase/firestore';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import { useMemo } from 'react';
import { getShipDateText } from 'utils/format';

const useActiveOrders = (limitNum = null, orderType = null) => {
  const orderQuery = useMemo(() => {
    if (orderType) {
      return query(
        collection(getFirestore(), 'orders'),
        where('status', '==', OrderStatus.IN_PROGRESS),
        where('isInternal', '==', orderType === OrderTypes.INTERNAL),
      );
    }
    return query(
      collection(getFirestore(), 'orders'),
      where('status', '==', OrderStatus.IN_PROGRESS),
    );
  }, [orderType]);

  const { data: activeOrders } = useRealtimeDocs({ query: orderQuery });

  return useMemo(() => {
    const orders = [];
    (activeOrders || []).forEach((order) => {
      (order?.shipments || []).forEach((shipment) => {
        if (shipment.isShipped) return;
        (shipment?.lineItems || []).forEach((lineItem) => {
          orders.push({
            lineItemId: lineItem.id,
            organization: order.organization,
            projectNumber: `${lineItem.projectId}${lineItem.pPartId ? `-${lineItem.pPartId}` : ''}`,
            projectId: lineItem.projectId,
            projectDocId: lineItem.projectDocId,
            shipDate: shipment.targetShipDate,
            orderDate: order.orderDate,
            orderName: order.name,
            steps: lineItem.steps,
            notes: lineItem.notes,
            // for product type lineItem
            type: lineItem.type,
            quantity: lineItem.quantity,
            quantityShipped: lineItem.quantityShipped || 0,
            sku: lineItem.sku,
          });
        });
      });
    });
    orders.sort((a, b) => new Date(a.shipDate).getTime() - new Date(b.shipDate).getTime());
    if (limitNum > 0) {
      return orders.slice(0, limitNum);
    }
    return orders;
  }, [activeOrders, limitNum]);
};

const useProjectStatus = (lineItemSteps, lineItemId = null) => {
  const travelersQuery = useMemo(() => {
    if (!lineItemId) return null;
    return query(
      collection(getFirestore(), 'travelers'),
      where('lineItem', '==', lineItemId),
    );
  }, [lineItemId]);

  const { data: travelers, status: queryStatus } = useRealtimeDocs({
    query: travelersQuery,
    transformData: travelerFromFirestore,
  });

  const projectStatus = useMemo(() => {
    if (queryStatus === 'success') {
      const initializePart = (step) => ({
        passed: 0,
        toBeInspected: 0,
        onDeck: 0,
        rejectCosmetic: 0,
        rejectMajor: 0,
        tuning: 0,
        unknown: 0,
        stepYield: 0,
        chipData: [],
        type: step.type,
      });
      // use index instead of name, because name is not unique
      const retParts = lineItemSteps?.map(initializePart) || {};
      if (!travelers.length) return retParts;

      travelers.forEach((traveler) => {
        let skipCalc = false;
        traveler.steps?.forEach((step, index) => {
          // for current step with inspected value, we still need to push to chipData for display
          if (skipCalc && step.inspected === InspectionValues.NOT_INSPECTED) return;

          /**
           * For travelers moved from Internal to Customer orders, step mismatches
           * with the line item may cause status calculation issues.
           * We skip any steps without a matching line item step
           * so aligned steps can still display status without errors.
           */
          if (!retParts[index]) return;

          const lastRun = step.runId?.[step.runId.length - 1];

          const addChipData = (curInspection) => {
            retParts[index].chipData.push({
              travelerId: traveler.id,
              prevInspection: traveler.steps[index - 1]?.isOnDeck ? 'On deck' : (traveler.steps[index - 1]?.inspected || InspectionValues.PASSED),
              curInspection,
              runStartDate: lastRun?.start?.toDate(),
              runEndDate: lastRun?.end?.toDate(),
              press: lastRun?.press,
              processId: lastRun?.id,
              cavity: lastRun?.cavity,
              // eslint-disable-next-line no-underscore-dangle
              sortDate: lastRun?.start || traveler._createdBy.timestamp,
              runID: lastRun?.runID,
              inspectedNote: step.inspectedNote,
              failureTags: step.failureTags,
            });
          };

          const addToDeck = () => {
            retParts[index].onDeck += 1;
            addChipData('On deck');
            // mark isOnDeck for next step display purpose
            // eslint-disable-next-line no-param-reassign
            step.isOnDeck = true;
            skipCalc = true;
          };

          // check if it's on deck for MOLDING and BONDING
          if (
            !skipCalc
            && !step.runId?.length
            && [StepTypes.MOLDING, StepTypes.BONDING].includes(step.type)
          ) {
            addToDeck();
            return;
          }

          if (step.inspected === InspectionValues.NOT_INSPECTED) {
            if (!skipCalc) {
              // for POSTMACHINING and CONNECTORIZING, we want show on deck instead of toBeInspected
              if ([StepTypes.POSTMACHINING, StepTypes.CONNECTORIZING].includes(step.type)) {
                addToDeck();
                return;
              }
              retParts[index].toBeInspected += 1;
            }
            skipCalc = true;
          } else if (step.inspected === InspectionValues.PASSED) {
            if (!skipCalc) retParts[index].passed += 1;
          } else if (step.inspected === InspectionValues.REJECT_MAJOR) {
            if (!skipCalc) retParts[index].rejectMajor += 1;
            skipCalc = true;
          } else if (step.inspected === InspectionValues.REJECT_COSMETIC) {
            if (!skipCalc) retParts[index].rejectCosmetic += 1;
            skipCalc = true;
          } else if (step.inspected === InspectionValues.TUNING) {
            // Calculate the turning count independently of the previous stage
            retParts[index].tuning += 1;
            skipCalc = true;
          } else {
            if (!skipCalc) retParts[index].unknown += 1;
            skipCalc = true;
          }
          addChipData(step.inspected);
        });
      });

      // calculate step yield rate
      Object.keys(retParts).forEach((key) => {
        const part = retParts[key];
        part.stepYield = Math.round(
          (part.passed / ((part.passed + part.rejectCosmetic + part.rejectMajor) || 1)) * 100,
        );
        // sort chipData by sortDate
        part.chipData.sort((a, b) => a.sortDate - b.sortDate);
      });

      // calculate total yield rate
      const totalY = Object.values(retParts).reduce(
        (acc, part) => acc * (part.stepYield / 100),
        1,
      );
      retParts.totalYield = Math.round(totalY * 100);
      return retParts;
    }
    return undefined;
  }, [queryStatus, lineItemSteps, travelers]);
  return { projectStatus, queryStatus };
};

const useShipDate = (targetShipDate, shipDateActual = null) => useMemo(() => {
  const baseText = getShipDateText(targetShipDate, shipDateActual);
  return baseText;
}, [shipDateActual, targetShipDate]);

export {
  useActiveOrders,
  useShipDate,
  useProjectStatus,
};
